/**
 * «Compras» in «Análisis» (the owner, 2026-10-13, of what the page could add: «let's build all of
 * it»): who sells each product cheapest, what is bought from each supplier and what is owed to
 * them, how much dearer what the shop buys has become month by month, and whether an offer to buy
 * more at once pays for the money it ties up.
 *
 * What a supplier charged for one base unit is the line's price without its share of the freight
 * and of the invoice's IVA (`precioDeLinea`), as «Subieron de precio» reads it: the freight is the
 * trip's, not the goods'. Voided purchases count nowhere. Pure, so the screen and its tests share
 * one answer.
 */

import { diasEntre, sumarDias, type Centavos, type Milli } from '@abastece/shared';
import type { Compra } from '@abastece/core';
import { precioDeLinea, type Tramo } from './analisis.js';
import { otroMes } from './historial.js';

/** How far back the suppliers' prices are compared: half a year. */
export const DIAS_DE_PROVEEDORES = 180;
/** The months «Lo que cuesta comprar» follows. */
export const MESES_DE_INDICE = 12;

const enOrden = (compras: readonly Compra[]): Compra[] =>
  compras.filter((c) => !c.anulada && c.lineas.length > 0).sort((a, b) => a.fecha.localeCompare(b.fecha) || a.id.localeCompare(b.id));

// ---------------------------------------------------------------------------
// Quién lo vende más barato
// ---------------------------------------------------------------------------

export interface PrecioDeProveedor {
  proveedorId: string;
  fecha: string;
  /** Centavos for one base unit. */
  precio: number;
}

export interface MasBarato {
  productoId: string;
  /** Whom it was bought from last, and at what. */
  ultimo: PrecioDeProveedor;
  /** Of the others' last prices, the lowest. */
  barato: PrecioDeProveedor;
  /** How much less, in basis points of the last price. */
  menosBp: number;
  /** What buying there would save a month, at what the shop bought of it a month the half year. */
  ahorroAlMes: Centavos;
}

/**
 * Products another supplier sold cheaper than the one bought from last: each supplier's last price
 * the half year, and the lowest of the others against the last one paid. What it would save a
 * month first.
 */
export function quienLoVendeMasBarato(compras: readonly Compra[], hoy: string): MasBarato[] {
  const desde = sumarDias(hoy, -(DIAS_DE_PROVEEDORES - 1));
  const productos = new Map<string, { porProveedor: Map<string, PrecioDeProveedor>; ultimo: PrecioDeProveedor; base: number; primera: string }>();
  for (const c of enOrden(compras)) {
    if (c.fecha < desde || c.fecha > hoy) continue;
    for (const l of c.lineas) {
      const precio = precioDeLinea(l);
      if (precio === null) continue;
      const este = { proveedorId: c.proveedorId, fecha: c.fecha, precio };
      const x = productos.get(l.productoId) ?? { porProveedor: new Map(), ultimo: este, base: 0, primera: c.fecha };
      x.porProveedor.set(c.proveedorId, este);
      x.ultimo = este;
      x.base += l.cantidadBase;
      productos.set(l.productoId, x);
    }
  }
  const lista: MasBarato[] = [];
  for (const [productoId, x] of productos) {
    const otros = [...x.porProveedor.values()].filter((p) => p.proveedorId !== x.ultimo.proveedorId);
    const barato = otros.sort((a, b) => a.precio - b.precio || b.fecha.localeCompare(a.fecha))[0];
    if (!barato || barato.precio >= x.ultimo.precio) continue;
    const meses = Math.max(1, (diasEntre(x.primera, hoy) + 1) / 30);
    lista.push({
      productoId,
      ultimo: x.ultimo,
      barato,
      menosBp: Math.round(((x.ultimo.precio - barato.precio) * 10_000) / x.ultimo.precio),
      ahorroAlMes: Math.round(((x.ultimo.precio - barato.precio) * x.base) / 1_000 / meses) as Centavos,
    });
  }
  return lista.sort((a, b) => b.ahorroAlMes - a.ahorroAlMes || b.menosBp - a.menosBp);
}

// ---------------------------------------------------------------------------
// A quién se le compra
// ---------------------------------------------------------------------------

export interface CompradoAProveedor {
  proveedorId: string;
  compras: number;
  /** What was bought from them in the period, less what went back. */
  total: Centavos;
  parteBp: number;
  /** What is owed to them now, of every purchase on credit. */
  debe: Centavos;
}

/** What the period bought from each supplier, its share, and what is owed to each now. The most bought first. */
export function comprasPorProveedor(compras: readonly Compra[], t: Tramo): CompradoAProveedor[] {
  const filas = new Map<string, { compras: number; total: number; debe: number }>();
  const fila = (id: string) => {
    const f = filas.get(id) ?? { compras: 0, total: 0, debe: 0 };
    filas.set(id, f);
    return f;
  };
  for (const c of compras) {
    if (c.anulada) continue;
    const debe = c.totalCordobas - c.pagado - (c.devueltoACuenta ?? 0);
    if (debe > 0) fila(c.proveedorId).debe += debe;
    if (c.fecha < t.desde || c.fecha > t.hasta) continue;
    const f = fila(c.proveedorId);
    f.compras += 1;
    f.total += c.totalCordobas - (c.devuelto ?? 0);
  }
  const total = [...filas.values()].reduce((a, f) => a + f.total, 0);
  return [...filas.entries()]
    .map(([proveedorId, f]) => ({
      proveedorId,
      compras: f.compras,
      total: f.total as Centavos,
      parteBp: total > 0 ? Math.round((f.total * 10_000) / total) : 0,
      debe: f.debe as Centavos,
    }))
    .sort((a, b) => b.total - a.total || b.debe - a.debe);
}

// ---------------------------------------------------------------------------
// Lo que cuesta comprar
// ---------------------------------------------------------------------------

export interface MesDeIndice {
  /** `YYYY-MM`. */
  mes: string;
  /** How much dearer than before what was bought this month came, in basis points; null when nothing could be compared. */
  cambioBp: number | null;
  /** Where the prices stand against the month before the first, at 10,000. */
  indiceBp: number;
}

/**
 * How much dearer what the shop buys has become, month by month: each product bought in a month at
 * its average price that month against the last month it was bought before, weighed by what was
 * spent on it, and the months chained. A product bought once a quarter counts when it is bought.
 * What comes back is the last `meses` months to `hoy`'s, and how far the last stands from before
 * the first.
 */
export function indiceDeCompras(compras: readonly Compra[], hoy: string, meses = MESES_DE_INDICE): { meses: MesDeIndice[]; totalBp: number | null } {
  // Each product's average price in each month it was bought.
  const porMes = new Map<string, Map<string, { pesos: number; base: number }>>();
  for (const c of enOrden(compras)) {
    if (c.fecha > hoy) continue;
    const mes = c.fecha.slice(0, 7);
    for (const l of c.lineas) {
      const precio = precioDeLinea(l);
      if (precio === null) continue;
      const delProducto = porMes.get(l.productoId) ?? new Map<string, { pesos: number; base: number }>();
      const x = delProducto.get(mes) ?? { pesos: 0, base: 0 };
      x.pesos += precio * l.cantidadBase;
      x.base += l.cantidadBase;
      delProducto.set(mes, x);
      porMes.set(l.productoId, delProducto);
    }
  }
  const ultimo = hoy.slice(0, 7);
  const lista = Array.from({ length: meses }, (_, i) => otroMes(ultimo, i - (meses - 1)));
  let indice = 10_000;
  let comparado = false;
  const salida: MesDeIndice[] = [];
  for (const mes of lista) {
    let pesos = 0;
    let suma = 0;
    for (const delProducto of porMes.values()) {
      const ahora = delProducto.get(mes);
      if (!ahora) continue;
      const antes = [...delProducto.keys()].filter((m) => m < mes).sort().at(-1);
      if (!antes) continue;
      const a = delProducto.get(antes)!;
      const precioAntes = a.pesos / a.base;
      const precioAhora = ahora.pesos / ahora.base;
      // Weighed by what was spent on it this month.
      const peso = ahora.pesos / 1_000;
      pesos += peso;
      suma += peso * (precioAhora / precioAntes - 1);
    }
    const cambio = pesos > 0 ? suma / pesos : null;
    if (cambio !== null) {
      indice *= 1 + cambio;
      comparado = true;
    }
    salida.push({ mes, cambioBp: cambio === null ? null : Math.round(cambio * 10_000), indiceBp: Math.round(indice) });
  }
  return { meses: salida, totalBp: comparado ? Math.round(indice) - 10_000 : null };
}

// ---------------------------------------------------------------------------
// ¿Conviene la oferta?
// ---------------------------------------------------------------------------

export interface Oferta {
  /** What the offer costs. */
  invertido: Centavos;
  /** What it saves against the usual price, and that in basis points of it. */
  ahorro: Centavos;
  ahorroBp: number;
  /** Days the offer's units take to sell at the pace, and with what is already there in front of them. */
  diasOferta: number | null;
  diasTotal: number | null;
  /**
   * What the saving earns a month on the money put in, in basis points: the money comes back as
   * the offer's units sell, after what is already there, so it is out half way through that on
   * average. Null with no pace to go by.
   */
  rindeBp: number | null;
}

/**
 * An offer to buy more at once (the supplier's «si lleva diez cajas…»): what it saves against the
 * usual price, how long it takes to sell at the pace the product goes, what is already there
 * first, and what the saving comes to a month on the money it ties up until then.
 */
export function ofertaPorVolumen(d: {
  /** Base units, in milli. */
  cantidad: Milli;
  /** Centavos for one base unit, offered and usual. */
  precioOferta: number;
  precioDeSiempre: number;
  /** Base units that leave a day, in milli (`salidaPorDia`). */
  porDia: number;
  /** What there is now, in base units, in milli. */
  existencia: number;
}): Oferta {
  const invertido = Math.round((d.precioOferta * d.cantidad) / 1_000);
  const ahorro = Math.round(((d.precioDeSiempre - d.precioOferta) * d.cantidad) / 1_000);
  const hay = Math.max(0, d.existencia);
  const vende = d.porDia > 0;
  const diasTotal = vende ? (hay + d.cantidad) / d.porDia : null;
  const medio = vende ? (hay / d.porDia + diasTotal!) / 2 : null;
  return {
    invertido: invertido as Centavos,
    ahorro: ahorro as Centavos,
    ahorroBp: d.precioDeSiempre > 0 ? Math.round(((d.precioDeSiempre - d.precioOferta) * 10_000) / d.precioDeSiempre) : 0,
    diasOferta: vende ? Math.round(d.cantidad / d.porDia) : null,
    diasTotal: diasTotal === null ? null : Math.round(diasTotal),
    rindeBp: medio !== null && invertido > 0 ? Math.round(((ahorro * 10_000) / invertido) * (30 / Math.max(1, medio))) : null,
  };
}
